import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { Home, CreditCard, FileText, BookOpen, TrendingUp, Settings, LogOut, MessageSquare, Activity, BarChart2 } from 'lucide-react'
import { useAuth } from '@hooks/useAuth'
import { useIsMobile } from '@hooks/useMediaQuery'
import { supabase } from '@lib/supabase'
import { cn } from '@lib/utils'
import MacroBanner from '@/components/dashboard/MacroBanner'
import { ThemeToggle } from '@components/shared/ThemeToggle'
import { ErrorBoundary } from '@components/shared/ErrorBoundary'

export default function DashboardLayout() {
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    checkAdmin()
  }, [user])

  const checkAdmin = async () => {
    if (!user) return

    try {
      const { data } = await supabase
        .from('user_preferences')
        .select('is_admin')
        .eq('user_id', user.id)
        .maybeSingle()

      setIsAdmin(data?.is_admin || false)
    } catch (err) {
      console.error('Error checking admin:', err)
    }
  }

  const baseMenuItems = [
    { icon: Home, label: 'Vue d\'ensemble', href: '/dashboard' },
    { icon: CreditCard, label: 'Abonnement', href: '/dashboard/subscription' },
    { icon: BookOpen, label: 'Mes journaux', href: '/dashboard/journals' },
    { icon: Activity, label: 'Comptes MT', href: '/dashboard/mt-accounts' },
    { icon: FileText, label: 'Factures', href: '/dashboard/invoices' },
    { icon: TrendingUp, label: 'Affiliation', href: '/dashboard/affiliate' },
    { icon: BarChart2, label: 'Economie', href: '/dashboard/economie' },
    { icon: Settings, label: 'Paramètres', href: '/dashboard/settings' },
  ]

  const adminMenuItems = [
    { icon: MessageSquare, label: 'Support', href: '/dashboard/support' },
  ]

  const menuItems = isAdmin ? [...baseMenuItems.slice(0, 5), ...adminMenuItems, ...baseMenuItems.slice(5)] : baseMenuItems

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === href
    }
    return location.pathname.startsWith(href)
  }

  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black pb-20">
        <div className="bg-white dark:bg-neutral-950 border-b dark:border-white/10 sticky top-0 z-10 px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <TrendingUp className="h-6 w-6 text-primary dark:text-white" />
              <span className="text-xl font-bold dark:text-white">TrackMarkete</span>
            </Link>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <button
                onClick={handleLogout}
                className="text-gray-600 dark:text-neutral-400 hover:text-gray-900 dark:text-neutral-300 dark:hover:text-white transition-colors p-2"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
        <MacroBanner />

        <div className="p-6">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>

        <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-950 border-t dark:border-white/10 grid grid-cols-4 gap-1 py-2 px-2">
          {menuItems.slice(0, 4).map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex flex-col items-center gap-1 py-2 rounded-lg transition-colors',
                  active ? 'text-primary bg-primary/10 dark:text-white dark:bg-white/10' : 'text-gray-600 dark:text-neutral-400'
                )}
              >
                <Icon size={20} />
                <span className="text-xs font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            )
          })}
        </nav>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex">
      <aside className="w-64 bg-white dark:bg-neutral-950 border-r dark:border-white/10 flex flex-col fixed left-0 top-0 bottom-0">
        <div className="p-6 border-b dark:border-white/10">
          <Link to="/" className="flex items-center gap-2 group">
            <TrendingUp className="h-8 w-8 text-primary dark:text-white group-hover:scale-110 transition-transform" />
            <span className="text-2xl font-bold dark:text-white">TrackMarkete</span>
          </Link>
        </div>

        <div className="p-4 border-b dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-white/10 flex items-center justify-center text-primary dark:text-white font-semibold">
              {user?.email?.[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate dark:text-neutral-100">{user?.email?.split('@')[0]}</div>
              <div className="text-xs text-gray-500 dark:text-neutral-400 truncate">{user?.email}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg transition-all',
                  active
                    ? 'bg-primary text-white dark:bg-white dark:text-black'
                    : 'text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-white/10'
                )}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <div className="p-4 border-t dark:border-white/10 flex items-center justify-between gap-2">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg flex-1 text-gray-700 dark:text-neutral-300 hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-white/10 transition-colors"
          >
            <LogOut size={20} />
            <span className="font-medium">Déconnexion</span>
          </button>
          <ThemeToggle />
        </div>
      </aside>

      <main className="flex-1 ml-64 flex flex-col">
        <MacroBanner />
        <div className="p-8">
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  )
}
